import React, { useState, useEffect } from 'react';
import { useConsentStore, TrackingCategory } from '@/stores/consentStore';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const ConsentPreferencesModal: React.FC = () => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const {
    consent,
    isPreferencesModalOpen,
    closePreferencesModal,
    setConsent,
    setAllConsents,
  } = useConsentStore();

  if (!isClient || !isPreferencesModalOpen) {
    return null;
  }

  const handleToggle = (category: TrackingCategory, checked: boolean) => {
    setConsent(category, checked ? 'granted' : 'denied');
  };

  const handleConfirmChoices = () => {
    closePreferencesModal();
    // Trackers should re-evaluate based on store subscription in tracking.ts
  };
  
  const handleAcceptAllModal = () => {
    setAllConsents('granted');
    closePreferencesModal();
  }

  const handleRejectAllModal = () => {
    setAllConsents('denied'); 
    closePreferencesModal();
  }

  const categories: Array<{ id: TrackingCategory; label: string; description: string, disabled?: boolean }> = [
    { id: 'functional', label: 'Functional Cookies', description: 'Essential for the website to function properly (e.g., remembering your session). These cannot be disabled.', disabled: true },
    { id: 'performance', label: 'Performance Cookies', description: 'Help us understand how visitors interact with our website by collecting and reporting information anonymously, allowing us to improve site performance.' },
    { id: 'analytics', label: 'Analytics Cookies', description: 'Allow us to count visits and traffic sources so we can measure and improve the performance and content of our site (e.g., Google Analytics).' },
    { id: 'advertising', label: 'Advertising/Marketing Cookies', description: 'Used to build a profile of your interests and show you relevant advertisements on other sites. Opting out may result in less targeted advertising.' },
  ];

  return (
    <Dialog open={isPreferencesModalOpen} onOpenChange={(open) => !open && closePreferencesModal()}>
      <DialogContent className="sm:max-w-lg md:max-w-xl"> {/* Slightly wider modal */}
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Cookie Preferences</DialogTitle>
          <DialogDescription className="text-sm mt-1">
            We use cookies to enhance your experience. You can manage your preferences below. Your choices are saved automatically. For more details, please see our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Privacy Policy</a>.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto pr-2 -mr-2 custom-scrollbar"> {/* Added custom-scrollbar if defined */}
          {categories.map(({ id, label, description, disabled }) => (
            <div key={id} className="flex items-start justify-between space-x-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <div className="space-y-1 flex-1">
                <Label htmlFor={`consent-${id}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {label}
                </Label>
                <p className="text-xs text-muted-foreground">{description}</p>
              </div>
              <Switch
                id={`consent-${id}`}
                checked={consent[id] === 'granted'}
                onCheckedChange={(checked) => handleToggle(id, checked)}
                disabled={disabled || id === 'functional'}
                aria-label={`Toggle ${label}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:flex-row sm:justify-between mt-2 pt-4 border-t">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                 <Button variant="outline" onClick={handleRejectAllModal} size="sm" className="w-full sm:w-auto">Reject Non-Essential</Button>
                 <Button onClick={handleAcceptAllModal} size="sm" className="w-full sm:w-auto">Accept All</Button>
            </div>
            <Button variant="default" onClick={handleConfirmChoices} size="sm" className="w-full sm:w-auto">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentPreferencesModal;
